import React, { useEffect, useMemo, useState } from 'react';
import {
    HiClipboardDocumentList,
    HiMagnifyingGlass,
} from 'react-icons/hi2';
import { TablePagination as Pagination } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { OrderService } from '../../service/manage/exam_subscription/order.service';
import downloadInvoicePDF from './InvoicePDf';
import { sweetStatusAlert } from '../../components/common/SweetAlert';
import { routes } from '../../helper/routes';
import OrdersTab from './OrdersTab';
import InvoicesTab from './InvoicesTab';
import './MyAccounts.css';
import './AccountSectionHeader.css';

const AccountManagement = ({ activeSection = 'orders' }) => {
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalInvoices, setTotalInvoices] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [limit, setLimit] = useState(5);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const history = useHistory();
    const location = useLocation();
    const routeExpandInvoiceId = location.state?.expandInvoiceId || null;
    const [expandInvoiceId, setExpandInvoiceId] = useState(routeExpandInvoiceId);

    useEffect(() => {
        setExpandInvoiceId(routeExpandInvoiceId);
    }, [routeExpandInvoiceId]);

    const fetchAdminOrdersData = async (search = '') => {
        try {
            const userType = sessionStorage.getItem('userType');
            const response = await OrderService.getAdminOrderDetails(
                limit, 
                offset, 
                search || undefined, 
                userType || undefined
            );
            if (response.success) {
                setOrders(response.data.orders || []);
                setTotalOrders(response.data.total_orders || 0);
            } else {
                setError(response.message || 'Failed to load orders.');
            }
        } catch (err) {
            setError('Failed to load orders. Please try again.');
            console.error('Orders fetch error:', err);
        }
    };

    const fetchAdminInvoicesData = async (search = '') => {
        try {
            const userType = sessionStorage.getItem('userType');
            const response = await OrderService.getAdminInvoiceDetails(
                limit, 
                offset, 
                search || undefined, 
                userType || undefined
            );
            if (response.success) {
                setInvoices(response.data.invoices || []);
                setTotalInvoices(response.data.total_invoices || 0);
            } else {
                setError(response.message || 'Failed to load invoices.');
            }
        } catch (err) {
            setError('Failed to load invoices. Please try again.');
            console.error('Invoices fetch error:', err);
        }
    };

    useEffect(() => {
        const fetchAccountDetails = async () => {
            setLoading(true);
            setError(null);
            
            try {
                if (activeSection === 'orders') {
                    await fetchAdminOrdersData(searchTerm);
                } else {
                    await fetchAdminInvoicesData(searchTerm);
                }
            } catch (err) {
                setError('Failed to load details. Please try again.');
                console.error('Account details fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAccountDetails();
    }, [offset, limit, searchTerm, activeSection]);

    useEffect(() => {
        setCurrentPage(0);
        setOffset(0);
    }, [activeSection]);

    const handlePageChange = (event, newPage) => {
        const newOffset = newPage * limit;
        setCurrentPage(newPage);
        setOffset(newOffset);
    };

    const handleRowsPerPageChange = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        const newPage = Math.floor(offset / newLimit);
        setLimit(newLimit);
        setCurrentPage(newPage);
        setOffset(newLimit * newPage);
    };

    const handleSearchChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(0);
        setOffset(0);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleViewInvoice = (orderId) => {
        const matchingInvoice = invoices.find(inv => inv.order_id === orderId);
        history.push(routes.admin_order_management_invoices, {
            expandInvoiceId: matchingInvoice?.invoice_id || null,
        });
    };

    const handleDownloadInvoice = async (invoice) => {
        try {
            await downloadInvoicePDF(null, invoice, formatDate);
        } catch (err) {
            console.error('Failed to download invoice:', err);
            sweetStatusAlert('Error', 'Failed to download invoice', 'error');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'completed';
            case 'pending': return 'pending';
            case 'cancelled': return 'cancelled';
            case 'processing': return 'processing';
            default: return 'pending';
        }
    };

    const getItemTypeClass = (type) => {
        return type === 'credit_package' ? 'credit_package' : 'assessment';
    };

    const getItemTypeLabel = (type) => {
        return type === 'credit_package' ? 'Credit Package' : 'Assessment';
    };

    const pageTitle = useMemo(() => {
        return activeSection === 'orders' ? 'Orders' : 'Invoices';
    }, [activeSection]);

    const pageSubtitle = useMemo(() => {
        if (activeSection === 'orders') {
            return `${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'} across the system`;
        }

        return `${totalInvoices} ${totalInvoices === 1 ? 'invoice' : 'invoices'} available to review`;
    }, [activeSection, totalInvoices, totalOrders]);

    const totalCount = activeSection === 'orders' ? parseInt(totalOrders) : parseInt(totalInvoices);

    if (loading) {
        return (
            <div className="my-accounts-page">
                <div className="my-accounts-content">
                    <p className="my-accounts-loading">Loading order management details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-accounts-page">
                <div className="my-accounts-content">
                    <div className="my-accounts-empty-state">
                        <HiClipboardDocumentList className="my-accounts-empty-icon" />
                        <h3 className="my-accounts-empty-title">Unable to load</h3>
                        <p className="my-accounts-empty-message">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-accounts-page">
            <div className="account-section-page-header">
                <div className="account-section-page-header-left">
                    <h1 className="account-section-page-title">{pageTitle}</h1>
                    <p className="account-section-page-subtitle">{pageSubtitle}</p>
                </div>
            </div>

            <div className="my-accounts-filters my-accounts-filters-modern">
                <div className="filter-group row g-3 align-items-stretch">
                    <div className="col-12 col-lg-9">
                    <input
                        type="text"
                        placeholder="Search by user name or product..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                        className="search-input my-accounts-search-input-modern"
                    />
                    </div>
                    <div className="col-12 col-lg-auto">
                        <button 
                            onClick={handleSearch}
                            className="search-button my-accounts-search-button-modern"
                        >
                            <HiMagnifyingGlass className="my-accounts-search-button-icon" />
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {activeSection === 'orders' ? (
                <OrdersTab
                    orders={orders}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    limit={limit}
                    totalOrders={totalOrders}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    formatDate={formatDate}
                    getStatusClass={getStatusClass}
                    getItemTypeClass={getItemTypeClass}
                    getItemTypeLabel={getItemTypeLabel}
                    onViewInvoice={handleViewInvoice}
                />
            ) : (
                <InvoicesTab
                    invoices={invoices}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    limit={limit}
                    totalInvoices={totalInvoices}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onDownloadInvoice={handleDownloadInvoice}
                    formatDate={formatDate}
                    getItemTypeClass={getItemTypeClass}
                    getItemTypeLabel={getItemTypeLabel}
                    expandInvoiceId={expandInvoiceId}
                />
            )}

            <div className="my-accounts-pagination">
                <Pagination
                    component="div"
                    count={totalCount}
                    page={currentPage}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[5, 10, 20]}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </div>
        </div>
    );
};

export const AdminOrdersPage = () => <AccountManagement activeSection="orders" />;
export const AdminInvoicesPage = () => <AccountManagement activeSection="invoices" />;

export default AdminOrdersPage;
